import React from "react";
import { motion } from "framer-motion";
import { useWallet } from "@/context/WalletContext";
import { usePolling } from "@/components/croc/hooks";
import { Panel, SectionTitle, Pill } from "@/components/croc/ui";
import { fmtUsd, pct } from "@/lib/format";
import api from "@/lib/api";
import { Crown, Check, Lock, Award, Percent, Users, Wallet, Sparkles, ArrowDown, Equal } from "lucide-react";

export default function Ranks() {
  const { user } = useWallet();
  const { data: config } = usePolling(() => api.config(), [], 120000);
  const ranks = config?.ranks ?? [];
  const currentLevel = user?.rank?.level ?? 0;
  const matching = user?.matching_volume ?? 0;
  const selfTopup = user?.self_topup ?? 0;
  const next = user?.next_rank;

  const progressToNext = next
    ? Math.min(100, (matching / next.matching) * 100)
    : 100;

  return (
    <div>
      <SectionTitle
        eyebrow="Apex Rank Hierarchy · 9 Tiers"
        title="Rank Hierarchy"
        subtitle="Climb from Hatchling to Apex Croc. Commission = Team ROI × your rank %, paid up the tree as a differential cascade. Ranks qualify on self top-up + matching (weakest-leg) volume."
        testid="ranks-title"
      />

      <Panel glow="gold" className="mb-6" data-testid="ranks-current">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-[#1CFD4C]/12 border border-[#1CFD4C]/30 grid place-items-center glow-gold">
              <Crown className="w-7 h-7 gold-text" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Current Rank</div>
              <div className="font-display font-black text-2xl text-white" data-testid="ranks-current-name">{user?.rank?.name ?? "Hatchling"}</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-6">
            <div>
              <div className="text-xs text-muted-foreground">Commission</div>
              <div className="font-num font-bold text-xl emerald-text" data-testid="ranks-current-commission">{pct((user?.rank?.commission_pct ?? 0) * 100, 0)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Matching (weakest leg)</div>
              <div className="font-num font-bold text-xl text-white" data-testid="ranks-current-matching">{fmtUsd(matching)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Self Top-up</div>
              <div className="font-num font-bold text-xl text-white" data-testid="ranks-current-selftopup">{fmtUsd(selfTopup)}</div>
            </div>
          </div>
        </div>
        {next && (
          <div className="mt-5 space-y-3">
            <div>
              <div className="flex items-center justify-between text-xs mb-2">
                <span className="text-muted-foreground">Matching to {next.name}</span>
                <span className="font-num gold-text">{fmtUsd(matching)} / {fmtUsd(next.matching)}</span>
              </div>
              <div className="h-2.5 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#10B981] to-[#1CFD4C]" style={{ width: `${progressToNext}%` }} data-testid="ranks-progress-bar" />
              </div>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Self top-up to {next.name}</span>
              <span className={`font-num ${selfTopup >= next.self_topup ? "emerald-text" : "text-muted-foreground"}`}>
                {fmtUsd(selfTopup)} / {fmtUsd(next.self_topup)}
              </span>
            </div>
          </div>
        )}
      </Panel>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {ranks.filter((r) => r.level > 0).map((r, i) => {
          const achieved = r.level <= currentLevel;
          const isCurrent = r.level === currentLevel;
          return (
            <motion.div
              key={r.level}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              className={`croc-panel p-5 ${isCurrent ? "shimmer-border glow-emerald" : ""}`}
              data-testid={`rank-card-${r.level}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className={`w-8 h-8 rounded-lg grid place-items-center ${achieved ? "bg-[#10B981]/15" : "bg-white/5"}`}>
                    {achieved ? <Check className="w-4 h-4 emerald-text" /> : <Lock className="w-4 h-4 text-muted-foreground" />}
                  </span>
                  <span className="font-display font-bold text-white">{r.name}</span>
                </div>
                {isCurrent && <Pill tone="emerald">Current</Pill>}
              </div>
              <div className="space-y-2 text-sm">
                <Row icon={Wallet} label="Self top-up req." value={fmtUsd(r.self_topup, 0)} />
                <Row icon={Users} label="Matching req." value={fmtUsd(r.matching, 0)} />
                <Row icon={Percent} label="Commission" value={pct(r.commission_pct * 100, 0)} />
                <Row icon={Crown} label="One-time reward" value={r.one_time_reward ? fmtUsd(r.one_time_reward, 0) : "—"} />
                <Row icon={Sparkles} label="Same-rank bonus" value={r.same_rank_bonus_pct ? pct(r.same_rank_bonus_pct * 100, 0) : "—"} />
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* worked example: differential cascade + same-rank bonus */}
      <Panel glow="gold" className="mt-6" data-testid="rank-explainer">
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="w-4 h-4 gold-text" />
          <span className="font-display font-bold text-white">How Rank Commission Flows</span>
        </div>
        <p className="text-xs text-muted-foreground mb-4">A downline claims <span className="font-num text-white">$100</span> in daily rewards. Here's what cascades up:</p>
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="rounded-xl border border-white/10 bg-[#090E0B] p-4" data-testid="explainer-step-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-lg bg-[#10B981]/15 grid place-items-center text-xs font-num emerald-text">1</span>
              <span className="text-sm text-white font-semibold">Nearest ranked upline</span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              A <span className="gold-text">Snapper (5%)</span> directly above pockets <span className="font-num emerald-text">$5.00</span> (5% × $100).
              Unranked uplines block the flow — ranks keep it moving.
            </p>
          </div>
          <div className="rounded-xl border border-white/10 bg-[#090E0B] p-4" data-testid="explainer-step-2">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-lg bg-[#10B981]/15 grid place-items-center text-xs font-num emerald-text"><ArrowDown className="w-3.5 h-3.5" /></span>
              <span className="text-sm text-white font-semibold">Differential above</span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              A <span className="gold-text">Basker (10%)</span> further up pockets the difference: <span className="font-num emerald-text">$5.00</span> more (10% − 5% × $100).
              One full slice max — never double-counted.
            </p>
          </div>
          <div className="rounded-xl border border-white/10 bg-[#090E0B] p-4" data-testid="explainer-step-3">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-lg bg-[#1CFD4C]/15 grid place-items-center text-xs font-num gold-text"><Equal className="w-3.5 h-3.5" /></span>
              <span className="text-sm text-white font-semibold">Same-rank bonus</span>
            </div>
            <p className="text-xs text-muted-foreground leading-relaxed">
              A <span className="gold-text">River Croc</span> earns <span className="font-num emerald-text">$15.00</span>; the <span className="gold-text">River Croc</span> above gets no differential,
              so they earn <span className="font-num gold-text">5%</span> of that commission: <span className="font-num emerald-text">$0.75</span>. Equal ranks still pay.
            </p>
          </div>
        </div>
      </Panel>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-1.5 text-muted-foreground"><Icon className="w-3.5 h-3.5" />{label}</span>
      <span className="font-num text-white">{value}</span>
    </div>
  );
}
